<script setup lang="ts">
import type { JournalListItemDTO } from '~/shared/api/client/journals/journals.dto';
import { useJournalOrder } from '../lib/useJournalOrder';

const props = defineProps<{
  issue: JournalListItemDTO;
  size: 'standard' | 'large';
  labelSize?: 12 | 14;
  equalWidth?: boolean;
  /** Mobile only: label at one edge of the button and the arrow at the other */
  spread?: boolean;
  /** Overrides the issue's own button name — cards use a short label */
  label?: string;
}>();

const buyLabel = computed(() => props.label ?? props.issue.button_name);

/*
 * The button stands in three places — the hero, a card of the grid and the
 * issue page — and the modal is mounted once, at the root of the section, so
 * the click goes through the shared state and not through an event of its own
 */
const { open } = useJournalOrder();
</script>

<template>
  <div
    :class="[
      $style.buttons,
      {
        [$style.buttons_equal!]: equalWidth,
        [$style.buttons_spread!]: spread,
        [$style.labels_large!]: labelSize === 14,
      },
    ]"
  >
    <Button
      theme="primary"
      :size="size"
      :class="$style.button"
      @click="open(issue)"
    >
      <span :class="$style.label">{{ buyLabel }}</span>
      <Icon name="my-icon:i-arrow-right" :class="$style.icon" />
    </Button>
    <Button
      v-if="issue.ozon_link"
      theme="secondary"
      :size="size"
      :to="issue.ozon_link"
      target="_blank"
      :class="$style.button"
    >
      <span :class="$style.label">Купить на OZON</span>
      <Icon name="my-icon:i-arrow-right" :class="$style.icon" />
    </Button>
    <!-- The issue page adds "Разместить рекламу" here, the cards add nothing -->
    <slot />
  </div>
</template>

<style module lang="scss">
.buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-1-5x);

  // Both buttons of a pair at the same width, up to the 190px of the layout
  &_equal {
    gap: var(--space-2x);
    width: 100%;

    > * {
      flex: 1 1 150px;
      max-width: 190px;
    }
  }

  /*
   * A card stacks its buttons, one under another, everywhere below the wide
   * layout at the end of this file. Half a cell holds the pair only in some of
   * the sizes — the news column takes the room back as it appears — so leaving
   * the choice to the space left over made them flip between a row and a
   * column as the window grew
   */
  &_spread {
    flex-direction: column;
    align-items: center;
    gap: var(--space-1x);

    > * {
      flex: 0 0 auto;
      width: 100%;
      max-width: 190px;
    }
  }
}

@media (max-width: 599px) {
  /*
   * On mobile the arrow of a card button stands at the far edge instead of
   * following the label. The wide row of the issue page keeps its label and
   * arrow together in the middle, the way that layout draws them
   */
  .buttons_spread .button > span {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }

  /*
   * The row of the issue page goes full width, one under another. It is the
   * only kind left to place here — a pair sets its own column further up, and
   * stretching it from here would pull its buttons off the middle of the card
   */
  .buttons:not(.buttons_equal) {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    gap: var(--space-1x);

    /*
     * The mobile layout draws these as the xl button, 60px tall, while the row
     * stays "standard" on desktop. The shared button takes its size from a
     * prop, which cannot follow a media query, so the height is restated here
     */
    > * {
      height: 60px;
      min-height: 60px;
    }
  }

  /*
   * Half a screen leaves no room for the standard button: on mobile the cards
   * are drawn at the height of the small one, with tighter padding. Only the
   * cards ask for "spread", so the hero row below keeps its own size
   */
  .buttons_spread .button {
    height: 32px;
    min-height: 32px;
    padding-left: var(--space-1x);
    padding-right: var(--space-1x);
    // The tightened tracking of the layout: label at -5%
    letter-spacing: -0.05em;
  }

  /*
   * The hero row has the whole screen to itself: the two buttons fill its
   * width one under another at 60px, keeping the 24px padding of the standard
   * size the layout draws them at. It stays a row — in a column the basis of a
   * button would set its height — and each of them takes a full line
   */
  .buttons_equal:not(.buttons_spread) {
    flex-direction: row;
    gap: var(--space-1x);

    > * {
      flex-basis: 100%;
      max-width: none;
      height: 60px;
      min-height: 60px;
    }
  }
}

/*
 * Only the widest layout leaves a card cell room for the pair side by side,
 * the way the desktop drawing has it
 */
@media (min-width: 1380px) {
  .buttons_spread {
    flex-direction: row;
    gap: var(--space-2x);

    > * {
      flex: 1 1 150px;
      max-width: 190px;
    }
  }
}

.button > span {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1x);
}

// List labels are 12px; the issue page opts into 14px at every screen width.
.label {
  @include text_button3;
  white-space: nowrap;
}

.labels_large .label {
  @include text_button2;
}

.icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;

  // The shared arrow ships with a hardcoded white fill — follow the button text
  path {
    fill: currentColor;
  }
}
</style>
